import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Table, Badge, Modal, Field, Input, Btn, useDirtyGuard, ConfirmClose } from "@/components/erp/ui";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Truck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { customer_name: "", address: "", phone: "", driver: "", vehicle: "", notes: "", date: "", items: [{ name: "", qty: 1, unit: "pcs", notes: "" }] };

export default function Deliveries() {
  const [rows, setRows] = useState([]);
  const [wos, setWos] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const g = useDirtyGuard();
  const forceClose = () => { g.clear(); setOpen(false); setForm(EMPTY); };
  const tryClose = () => g.attemptClose(form, forceClose);

  const load = () => api.get("/deliveries").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); api.get("/workorders").then(({ data }) => setWos(data)).catch(() => {}); }, []);

  const addItem = () => setForm({ ...form, items: [...form.items, { name: "", qty: 1, unit: "pcs", notes: "" }] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });
  const setItem = (i, k, v) => { const it = [...form.items]; it[i] = { ...it[i], [k]: v }; setForm({ ...form, items: it }); };

  const save = async () => {
    if (!form.customer_name || form.items.every((i) => !i.name)) { toast.error("Isi penerima & minimal 1 barang"); return; }
    try {
      await api.post("/deliveries", { ...form, items: form.items.filter((i) => i.name).map((i) => ({ ...i, qty: +i.qty })) });
      toast.success("Surat Jalan dibuat"); setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Gagal membuat surat jalan"); }
  };
  const fromWO = async (wid) => {
    if (!wid) return;
    try { await api.post(`/deliveries/from-workorder/${wid}`); toast.success("Surat Jalan dibuat dari Work Order"); load(); }
    catch { toast.error("Gagal"); }
  };
  const viewPDF = (d) => window.open(`${BACKEND}/api/deliveries/${d.id}/pdf`, "_blank");

  return (
    <div>
      <PageHeader title="Pengiriman" subtitle="Surat Jalan otomatis untuk pengiriman barang ke customer." action={
        <div className="flex items-center gap-2">
          <select data-testid="delivery-from-wo" onChange={(e) => { fromWO(e.target.value); e.target.value = ""; }} className="h-10 rounded-lg border border-slate-300 px-3 text-sm bg-white">
            <option value="">+ dari Work Order…</option>
            {wos.map((w) => <option key={w.id} value={w.id}>{w.code} · {w.customer_name || "—"}</option>)}
          </select>
          <Btn data-testid="add-delivery-btn" onClick={() => { setForm(EMPTY); g.snapshot(EMPTY); setOpen(true); }}><Plus className="w-4 h-4" /> Surat Jalan</Btn>
        </div>} />
      <Table testid="deliveries-table" columns={["No. SJ", "Tanggal", "Penerima", "Driver", "Status", ""]} rows={rows} renderRow={(d) => (
        <tr key={d.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`delivery-row-${d.id}`}>
          <td className="px-4 py-3 font-mono text-xs">{d.code}</td>
          <td className="px-4 py-3">{d.date}</td>
          <td className="px-4 py-3 font-medium">{d.customer_name}</td>
          <td className="px-4 py-3 text-slate-500">{d.driver || "-"}</td>
          <td className="px-4 py-3"><Badge>{d.status}</Badge></td>
          <td className="px-4 py-3 text-right"><button data-testid={`delivery-pdf-${d.id}`} onClick={() => viewPDF(d)} className="text-sky-600 text-sm hover:underline inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Surat Jalan</button></td>
        </tr>
      )} />

      <Modal open={open} onClose={tryClose} title="Surat Jalan Baru"
        footer={<><Btn variant="ghost" onClick={tryClose}>Batal</Btn><Btn data-testid="save-delivery-btn" onClick={save}><Truck className="w-4 h-4" /> Buat</Btn></>}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Penerima *"><Input data-testid="dv-customer" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></Field>
            <Field label="Telepon"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <div className="col-span-2"><Field label="Alamat"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>
            <Field label="Driver"><Input value={form.driver} onChange={(e) => setForm({ ...form, driver: e.target.value })} /></Field>
            <Field label="Kendaraan"><Input value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} /></Field>
            <Field label="Tanggal"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2"><span className="text-xs font-mono text-slate-500">BARANG</span><Btn variant="ghost" data-testid="dv-add-item" onClick={addItem}><Plus className="w-4 h-4" /> Barang</Btn></div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_60px_70px_28px] gap-2 items-center" data-testid={`dv-item-${i}`}>
                  <Input placeholder="Nama barang" value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} />
                  <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                  <Input placeholder="Satuan" value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} />
                  <button onClick={() => rmItem(i)} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <Field label="Catatan"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
      </Modal>
      <ConfirmClose open={g.confirmOpen} testid="delivery-confirm-close" onCancel={() => g.setConfirmOpen(false)} onDiscard={forceClose} onSave={async () => { g.setConfirmOpen(false); await save(); }} />
    </div>
  );
}
